import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Trans, useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  ArrowRight,
  Camera,
  Check,
  CircleAlert,
  Link2,
  Loader2,
  User,
  Users,
  X,
} from 'lucide-react'
import { isAxiosError } from 'axios'
import { DateTime } from 'luxon'
import { AuthSplitLayout } from './AuthSplitLayout'
import {
  AUTH_BUTTON_CLASS,
  AuthHeader,
  AuthInput,
  AuthLabel,
  FieldError,
  TextButton,
} from './AuthFormParts'
import { InviteEmailInput } from './InviteEmailInput'
import { OnboardingPreview, UserAvatar } from './OnboardingPreview'
import type { OnboardingPreviewStage } from './OnboardingPreview'
import type { ReactNode } from 'react'
import type { AxiosResponse } from 'axios'
import type { PostGetCurrentUserResInterface } from '@/lib/api/features/authentication/types/PostGetCurrentUserResInterface'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import postCompleteOnboarding from '@/lib/api/features/authentication/api/postCompleteOnboarding'
import postGetCurrentUser from '@/lib/api/features/authentication/api/postGetCurrentUser'
import uploadProfilePicture from '@/lib/api/features/profile/api/uploadProfilePicture'
import { createInvite } from '@/lib/api/features/workspace-invites/api/createInvite'
import useLoginStore from '@/store/LoginStore'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'
import { setUserContext } from '@/lib/monitoring/sentry'

type WorkspaceModeChoice = 'personal' | 'team'
type Stage = OnboardingPreviewStage

interface TaskRow {
  id: number
  value: string
}

interface OnboardingWizardProps {
  userEmail: string
  initialData: {
    name?: string
  }
  isInviteFlow: boolean
}

const MAX_FIRST_TASKS = 5
const TOTAL_STEPS = 3
const WORKSPACE_NAME_MAX = 60
const SUGGESTION_KEYS = [
  'onboarding.tasks.suggestion1',
  'onboarding.tasks.suggestion2',
  'onboarding.tasks.suggestion3',
] as const

const STEP_NUMBER: Record<Stage, number> = {
  profile: 1,
  workspace: 2,
  // Invite is a sub-step of "workspace", so the progress still reads 2 of 3.
  invite: 2,
  tasks: 3,
}

function ErrorBanner({ children }: { children: ReactNode }) {
  return (
    <div role="alert" className="flex items-start gap-2 rounded-lg bg-destructive/10 px-3 py-2.5 text-sm text-destructive">
      <CircleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </div>
  )
}

function StepProgress({ step }: { step: number }) {
  const { t } = useTranslation('auth')
  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-1.5" aria-hidden="true">
        {Array.from({ length: TOTAL_STEPS }, (_, i) => (
          <span
            key={i}
            className={cn(
              'h-1.5 rounded-full transition-all duration-300',
              i + 1 === step ? 'w-6 bg-foreground' : 'w-3 bg-foreground/15',
            )}
          />
        ))}
      </div>
      <span className="text-xs font-medium text-muted-foreground">
        {t('onboarding.stepOf', { step, total: TOTAL_STEPS })}
      </span>
    </div>
  )
}

function ModeCard({
  selected,
  onSelect,
  icon,
  title,
  description,
  points,
}: {
  selected: boolean
  onSelect: () => void
  icon: ReactNode
  title: string
  description: string
  points: Array<string>
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        'relative flex flex-col gap-3 rounded-xl border bg-card p-4 text-left transition-[border-color,box-shadow] outline-none',
        'focus-visible:ring-[3px] focus-visible:ring-ring/50',
        selected ? 'border-foreground ring-1 ring-foreground' : 'border-border hover:border-foreground/30',
      )}
    >
      <span className="flex size-8 items-center justify-center rounded-lg bg-secondary text-secondary-foreground">
        {icon}
      </span>
      {selected ? (
        <span className="absolute right-3 top-3 flex size-5 items-center justify-center rounded-full bg-foreground text-background">
          <Check className="size-3" strokeWidth={3} />
        </span>
      ) : null}
      <span className="space-y-1">
        <span className="block text-[15px] font-semibold">{title}</span>
        <span className="block text-[13px] text-muted-foreground">{description}</span>
      </span>
      <ul className="space-y-1 text-[13px] text-secondary-foreground">
        {points.map((point) => (
          <li key={point} className="flex items-start gap-1.5">
            <Check className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
            {point}
          </li>
        ))}
      </ul>
    </button>
  )
}

export function OnboardingWizard({ userEmail, initialData, isInviteFlow }: OnboardingWizardProps) {
  const navigate = useNavigate()
  const { t } = useTranslation('auth')
  const [stage, setStage] = useState<Stage>('profile')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [profilePhotoPreview, setProfilePhotoPreview] = useState<string | null>(null)
  const [profilePhotoFile, setProfilePhotoFile] = useState<File | null>(null)
  const [workspaceMode, setWorkspaceMode] = useState<WorkspaceModeChoice>('personal')
  const [workspaceName, setWorkspaceName] = useState('')
  const [inviteEmails, setInviteEmails] = useState<Array<string>>([])
  const nextTaskId = useRef(1)
  const [taskRows, setTaskRows] = useState<Array<TaskRow>>([{ id: 0, value: '' }])
  const [focusTaskId, setFocusTaskId] = useState<number | null>(null)
  const taskInputRefs = useRef(new Map<number, HTMLInputElement>())
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Store setters
  const setStoreUsername = useLoginStore((state) => state.setUsername)
  const setStoreUserId = useLoginStore((state) => state.setUserId)
  const setStoreWorkspaceId = useLoginStore((state) => state.setWorkspaceId)
  const setWorkspaceKey = useLoginStore((state) => state.setWorkspaceKey)
  const setWorkspaceSec = useLoginStore((state) => state.setWorkspaceSec)
  const setStoreWorkspaceSlug = useLoginStore((state) => state.setWorkspaceSlug)
  const setWorkspaceType = useLoginStore((state) => state.setWorkspaceType)
  const setStoreUserRole = useLoginStore((state) => state.setUserRole)

  const onboardingSchema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .trim()
          .min(1, t('onboarding.profile.nameRequired'))
          .max(60, t('onboarding.profile.nameTooLong')),
      }),
    [t],
  )

  const form = useForm<z.infer<typeof onboardingSchema>>({
    resolver: zodResolver(onboardingSchema),
    defaultValues: {
      name: initialData.name || '',
    },
  })
  const liveName = form.watch('name')

  useEffect(() => {
    if (focusTaskId === null) return
    taskInputRefs.current.get(focusTaskId)?.focus()
    setFocusTaskId(null)
  }, [focusTaskId, taskRows])

  const goTo = (next: Stage) => {
    setError(null)
    setStage(next)
  }

  // --- First tasks -------------------------------------------------------

  const newRow = (value = ''): TaskRow => ({ id: nextTaskId.current++, value })

  const handleTaskNameChange = (id: number, value: string) => {
    setTaskRows((prev) => prev.map((row) => (row.id === id ? { ...row, value } : row)))
  }
  const handleAddTaskAfter = (id: number) => {
    if (taskRows.length >= MAX_FIRST_TASKS) return
    const row = newRow()
    setTaskRows((prev) => {
      const index = prev.findIndex((r) => r.id === id)
      return [...prev.slice(0, index + 1), row, ...prev.slice(index + 1)]
    })
    setFocusTaskId(row.id)
  }
  const handleRemoveTask = (id: number) => {
    if (taskRows.length === 1) {
      setTaskRows((prev) => prev.map((row) => ({ ...row, value: '' })))
      return
    }
    const index = taskRows.findIndex((r) => r.id === id)
    const next = taskRows.filter((r) => r.id !== id)
    setTaskRows(next)
    const focusTarget = next[Math.max(0, index - 1)]
    if (focusTarget) setFocusTaskId(focusTarget.id)
  }
  const handleSuggestion = (text: string) => {
    const empty = taskRows.find((row) => row.value.trim().length === 0)
    if (empty) {
      handleTaskNameChange(empty.id, text)
      return
    }
    if (taskRows.length >= MAX_FIRST_TASKS) return
    setTaskRows((prev) => [...prev, newRow(text)])
  }

  // --- Profile -----------------------------------------------------------

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      setError(t('onboarding.profile.photoNotImage'))
      return
    }

    if (file.size > 2 * 1024 * 1024) {
      setError(t('onboarding.profile.photoTooLarge'))
      return
    }

    setProfilePhotoFile(file)
    const reader = new FileReader()
    reader.onloadend = () => {
      setProfilePhotoPreview(reader.result as string)
      setError(null)
    }
    reader.readAsDataURL(file)
  }

  const handleProfileNext = form.handleSubmit(() => {
    setError(null)
    if (isInviteFlow) {
      void handleFinalSubmit()
    } else {
      setStage('workspace')
    }
  })

  // --- Submit ------------------------------------------------------------

  const sendInvites = async (workspaceSlug: string, emails: Array<string>) => {
    const results = await Promise.allSettled(
      emails.map((email) =>
        createInvite(workspaceSlug, { email, role: 'member', expirationDays: 7 }),
      ),
    )
    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        logger.logError(
          result.reason instanceof Error ? result.reason : new Error(String(result.reason)),
          LogCategory.AUTH_ONBOARDING,
          { note: 'Invite failed during onboarding, user can retry in Settings → Members', email: emails[index] },
        )
      }
    })
    return results.filter((r) => r.status === 'fulfilled').length
  }

  const handleFinalSubmit = async (skipTasks = false) => {
    if (isSubmitting) return
    setIsSubmitting(true)
    setError(null)

    const data = form.getValues()

    try {
      const detectedTimezone = DateTime.local().zoneName || 'UTC'

      // If user arrived via invite link, pass the token so the backend
      // joins them to the invited workspace instead of creating a new one.
      const pendingInviteToken = sessionStorage.getItem('pendingInviteToken')
        || localStorage.getItem('pendingInviteToken')

      const trimmedFirstTasks = skipTasks
        ? []
        : taskRows
            .map((row) => row.value.trim())
            .filter((value) => value.length > 0)
            .slice(0, MAX_FIRST_TASKS)

      const isTeam = workspaceMode === 'team'
      const trimmedWorkspaceName = workspaceName.trim()
      const invitesToSend = !pendingInviteToken && isTeam ? inviteEmails : []

      await postCompleteOnboarding({
        email: userEmail,
        name: data.name.trim(),
        timeZone: detectedTimezone,
        ...(pendingInviteToken
          ? { inviteToken: pendingInviteToken }
          : {
              workspaceType: isTeam ? 2 : 1,
              ...(isTeam && trimmedWorkspaceName
                ? { workspaceName: trimmedWorkspaceName }
                : {}),
              ...(trimmedFirstTasks.length > 0
                ? { firstTaskNames: trimmedFirstTasks }
                : {}),
            }),
      })

      const resp: AxiosResponse<PostGetCurrentUserResInterface> =
        await postGetCurrentUser()
      const user = resp.data

      // Update store
      setStoreUsername(user.username)
      setStoreUserId(user.userId)
      setStoreWorkspaceId(user.workspaceId)
      setStoreWorkspaceSlug(user.workspaceSlug)
      setWorkspaceKey(user.workspaceKey)
      setWorkspaceSec(user.workspaceSec)
      setWorkspaceType(user.workspaceType)
      setStoreUserRole(user.role ?? 'member')

      setUserContext({
        id: user.userId,
        email: user.username,
        workspaceSlug: user.workspaceSlug,
      })

      // Upload profile picture (non-blocking)
      if (profilePhotoFile) {
        try {
          await uploadProfilePicture({ file: profilePhotoFile })
        } catch (uploadErr) {
          logger.logError(
            uploadErr as Error,
            LogCategory.AUTH_ONBOARDING,
            { note: 'Profile picture upload failed during onboarding, user can retry in settings' }
          )
        }
      }

      // Invites need the workspace to exist, so they go out last. Failures
      // are logged and never block getting the user into their workspace.
      const invitesSent =
        invitesToSend.length > 0 ? await sendInvites(user.workspaceSlug, invitesToSend) : 0

      logger.logEvent(LogCategory.AUTH_ONBOARDING, 'completed', {
        userId: user.userId,
        workspaceSlug: user.workspaceSlug,
        name: data.name.trim(),
        timezone: detectedTimezone,
        workspaceMode,
        firstTaskCount: trimmedFirstTasks.length,
        inviteCount: invitesToSend.length,
        invitesSent,
      })

      // Clear invite token if present — invite was already accepted by the backend
      sessionStorage.removeItem('pendingInviteToken')
      localStorage.removeItem('pendingInviteToken')

      // The workspace root is Home; first-run users land in their Inbox.
      navigate({
        to: `/${user.workspaceSlug}/tasks`,
        replace: true,
      })
    } catch (err) {
      logger.logError(
        err as Error,
        LogCategory.AUTH_ONBOARDING,
        { email: userEmail }
      )

      if (isAxiosError(err)) {
        const apiMessage =
          (err.response?.data as { message?: string } | undefined)?.message ??
          t('onboarding.errors.createFailed')
        setError(apiMessage)
      } else {
        setError(t('onboarding.errors.unexpected'))
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  // --- Render ------------------------------------------------------------

  const submittingLabel = (
    <>
      <Loader2 className="size-4 animate-spin" />
      {t('onboarding.settingUp')}
    </>
  )

  const backButton = (to: Stage) => (
    <Button
      type="button"
      variant="outline"
      className={cn(AUTH_BUTTON_CLASS, 'bg-card dark:bg-card')}
      disabled={isSubmitting}
      onClick={() => goTo(to)}
    >
      <ArrowLeft className="size-4" />
      {t('onboarding.back')}
    </Button>
  )

  const displayWorkspaceName = workspaceName.trim() || t('onboarding.invite.fallbackName')
  const filledTaskCount = taskRows.filter((row) => row.value.trim()).length

  const footer = (
    <p>
      <Trans
        t={t}
        i18nKey="onboarding.profile.terms"
        components={{
          terms: (
            <a
              href="https://moly.so/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground underline underline-offset-4"
            />
          ),
          privacy: (
            <a
              href="https://moly.so/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground underline underline-offset-4"
            />
          ),
        }}
      />
    </p>
  )

  return (
    <AuthSplitLayout
      wide={stage !== 'profile'}
      footer={footer}
      preview={
        <OnboardingPreview
          stage={stage}
          name={liveName}
          photoUrl={profilePhotoPreview}
          mode={workspaceMode}
          workspaceName={workspaceName}
          invitees={inviteEmails}
          tasks={taskRows.map((row) => row.value)}
        />
      }
    >
      <div className="space-y-7">
        {!isInviteFlow && <StepProgress step={STEP_NUMBER[stage]} />}

        {/* Step 1: Profile */}
        {stage === 'profile' && (
          <form onSubmit={handleProfileNext} className="space-y-7" noValidate>
            <AuthHeader title={t('onboarding.profile.title')} subtitle={t('onboarding.profile.subtitle')} />

            <div className="flex items-center gap-4">
              <div className="relative">
                <UserAvatar name={liveName} photoUrl={profilePhotoPreview} className="size-16 text-xl" />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute -bottom-0.5 -right-0.5 flex size-6 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-xs hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={t('onboarding.profile.uploadAria')}
                >
                  <Camera className="size-3.5" />
                </button>
              </div>
              <div className="space-y-0.5">
                <TextButton className="text-sm" onClick={() => fileInputRef.current?.click()}>
                  {t('onboarding.profile.upload')}
                </TextButton>
                <p className="text-[13px] text-muted-foreground">{t('onboarding.profile.uploadHint')}</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="hidden"
                aria-label={t('onboarding.profile.uploadAria')}
              />
            </div>

            <div className="grid gap-2">
              <AuthLabel htmlFor="name">{t('onboarding.profile.nameLabel')}</AuthLabel>
              <AuthInput
                id="name"
                type="text"
                placeholder={t('onboarding.profile.namePlaceholder')}
                autoFocus
                autoComplete="name"
                aria-invalid={Boolean(form.formState.errors.name) || undefined}
                aria-describedby={form.formState.errors.name ? 'name-error' : undefined}
                {...form.register('name')}
              />
              {form.formState.errors.name?.message ? (
                <FieldError id="name-error">{form.formState.errors.name.message}</FieldError>
              ) : null}
            </div>

            {error && <ErrorBanner>{error}</ErrorBanner>}

            <Button type="submit" className={cn(AUTH_BUTTON_CLASS, 'w-full')} disabled={isSubmitting}>
              {isSubmitting ? (
                submittingLabel
              ) : isInviteFlow ? (
                t('onboarding.profile.join')
              ) : (
                <>
                  {t('onboarding.continue')}
                  <ArrowRight className="size-4" />
                </>
              )}
            </Button>
          </form>
        )}

        {/* Step 2: Personal or Team */}
        {stage === 'workspace' && (
          <form
            className="space-y-7"
            onSubmit={(e) => {
              e.preventDefault()
              goTo(workspaceMode === 'team' ? 'invite' : 'tasks')
            }}
          >
            <AuthHeader title={t('onboarding.mode.title')} subtitle={t('onboarding.mode.subtitle')} />

            <div role="radiogroup" aria-label={t('onboarding.mode.title')} className="grid grid-cols-1 gap-3 min-[420px]:grid-cols-2">
              <ModeCard
                selected={workspaceMode === 'personal'}
                onSelect={() => setWorkspaceMode('personal')}
                icon={<User className="size-4" />}
                title={t('onboarding.mode.personal')}
                description={t('onboarding.mode.personalDescription')}
                points={[t('onboarding.mode.personalPoint1'), t('onboarding.mode.personalPoint2')]}
              />
              <ModeCard
                selected={workspaceMode === 'team'}
                onSelect={() => setWorkspaceMode('team')}
                icon={<Users className="size-4" />}
                title={t('onboarding.mode.team')}
                description={t('onboarding.mode.teamDescription')}
                points={[t('onboarding.mode.teamPoint1'), t('onboarding.mode.teamPoint2')]}
              />
            </div>

            {workspaceMode === 'team' && (
              <div className="grid gap-2">
                <AuthLabel htmlFor="workspace-name">{t('onboarding.mode.workspaceNameLabel')}</AuthLabel>
                <AuthInput
                  id="workspace-name"
                  type="text"
                  placeholder={t('onboarding.mode.workspaceNamePlaceholder')}
                  autoComplete="organization"
                  maxLength={WORKSPACE_NAME_MAX}
                  value={workspaceName}
                  onChange={(e) => setWorkspaceName(e.target.value)}
                />
              </div>
            )}

            {error && <ErrorBanner>{error}</ErrorBanner>}

            <div className="flex gap-2.5">
              {backButton('profile')}
              <Button type="submit" className={cn(AUTH_BUTTON_CLASS, 'flex-1')}>
                {t('onboarding.continue')}
                <ArrowRight className="size-4" />
              </Button>
            </div>
          </form>
        )}

        {/* Step 2b (Team only): invite people */}
        {stage === 'invite' && (
          <div className="space-y-7">
            <AuthHeader
              title={t('onboarding.invite.title', { name: displayWorkspaceName })}
              subtitle={t('onboarding.invite.subtitle')}
            />

            <div className="grid gap-2">
              <AuthLabel htmlFor="invite-emails">{t('onboarding.invite.label')}</AuthLabel>
              <InviteEmailInput
                id="invite-emails"
                emails={inviteEmails}
                onChange={setInviteEmails}
                exclude={[userEmail]}
                disabled={isSubmitting}
              />
              <p className="flex items-start gap-1.5 text-[13px] text-muted-foreground">
                <Link2 className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                {t('onboarding.invite.linkNote')}
              </p>
            </div>

            {error && <ErrorBanner>{error}</ErrorBanner>}

            <div className="flex gap-2.5">
              {backButton('workspace')}
              <Button
                type="button"
                variant="ghost"
                className={AUTH_BUTTON_CLASS}
                onClick={() => {
                  setInviteEmails([])
                  goTo('tasks')
                }}
              >
                {t('onboarding.skip')}
              </Button>
              <Button type="button" className={cn(AUTH_BUTTON_CLASS, 'flex-1')} onClick={() => goTo('tasks')}>
                {inviteEmails.length > 0
                  ? t('onboarding.invite.send', { count: inviteEmails.length })
                  : t('onboarding.invite.sendNone')}
                <ArrowRight className="size-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: First tasks */}
        {stage === 'tasks' && (
          <div className="space-y-7">
            <AuthHeader title={t('onboarding.tasks.title')} subtitle={t('onboarding.tasks.subtitle')} />

            <div className="space-y-2">
              <span className="sr-only">{t('onboarding.tasks.label')}</span>
              {taskRows.map((row, index) => (
                <div key={row.id} className="flex items-center gap-1.5">
                  <AuthInput
                    ref={(el) => {
                      if (el) taskInputRefs.current.set(row.id, el)
                      else taskInputRefs.current.delete(row.id)
                    }}
                    id={`firstTaskName-${index}`}
                    type="text"
                    aria-label={`${t('onboarding.tasks.label')} ${index + 1}`}
                    placeholder={index === 0 ? t('onboarding.tasks.placeholderFirst') : t('onboarding.tasks.placeholder')}
                    autoFocus={index === 0}
                    maxLength={500}
                    value={row.value}
                    disabled={isSubmitting}
                    onChange={(e) => handleTaskNameChange(row.id, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                        e.preventDefault()
                        if (row.value.trim()) handleAddTaskAfter(row.id)
                      } else if (e.key === 'Backspace' && row.value === '' && taskRows.length > 1) {
                        e.preventDefault()
                        handleRemoveTask(row.id)
                      }
                    }}
                  />
                  {taskRows.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-[42px] shrink-0 text-muted-foreground"
                      aria-label={t('onboarding.tasks.remove', { index: index + 1 })}
                      onClick={() => handleRemoveTask(row.id)}
                      disabled={isSubmitting}
                    >
                      <X className="size-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">{t('onboarding.tasks.suggestionsLabel')}</p>
              <div className="flex flex-wrap gap-2">
                {SUGGESTION_KEYS.map((key) => {
                  const text = t(key)
                  const alreadyAdded = taskRows.some((row) => row.value.trim() === text)
                  const noRoom = taskRows.length >= MAX_FIRST_TASKS && taskRows.every((row) => row.value.trim())
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleSuggestion(text)}
                      disabled={isSubmitting || alreadyAdded || noRoom}
                      className="rounded-full border border-border bg-card px-3 py-1 text-[13px] text-secondary-foreground transition-colors hover:border-foreground/30 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                    >
                      {text}
                    </button>
                  )
                })}
              </div>
            </div>

            {error && <ErrorBanner>{error}</ErrorBanner>}

            <div className="flex gap-2.5">
              {backButton(workspaceMode === 'team' ? 'invite' : 'workspace')}
              <Button
                type="button"
                variant="ghost"
                className={AUTH_BUTTON_CLASS}
                disabled={isSubmitting}
                onClick={() => void handleFinalSubmit(true)}
              >
                {t('onboarding.skip')}
              </Button>
              <Button
                type="button"
                className={cn(AUTH_BUTTON_CLASS, 'flex-1')}
                disabled={isSubmitting}
                onClick={() => void handleFinalSubmit(filledTaskCount === 0)}
              >
                {isSubmitting ? (
                  submittingLabel
                ) : (
                  <>
                    {t('onboarding.tasks.submit')}
                    <ArrowRight className="size-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </AuthSplitLayout>
  )
}
